/**
 * The pe3d Design tab: the playEngine Design tab (components/playEngineDesign/)
 * over a pe3d release — the same outliner, rows, dialogs and look, fed by the
 * release's schema instead of a snapshot's globalVariables.
 *
 * A design is the company's layout of one game release on this base: groups,
 * order, labels, controls (server controller/pe3d/designStore.js). It holds no
 * values. Every row reads and writes the variant as the edited language
 * resolves it (`editor.view` / `editor.editView`), so a value set here is the
 * same value the Config, Assets and Playable tabs show — language overrides
 * and validation included. Types and bounds come from the schema
 * (modules/pe3dDesign.js).
 *
 * With no design yet, the release's own (bundle.json `design`) or a generated
 * one — assets by kind, the config as the schema groups it, playable settings —
 * is the start; nothing is stored until it is changed. Every change saves at
 * once against the hash it was read with: a design changed elsewhere is
 * reloaded, never overwritten.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, Tooltip } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import GenerateIcon from "@material-ui/icons/Build";
import * as api from "../../modules/pe3dApi";
import {
  addField,
  addGroup,
  designHasFieldPath,
  fieldAtPath,
  groupContents,
  moveField,
  moveGroup,
  normalizeDesign,
  removeField,
  removeGroup,
  renameGroup,
  reorderField,
  updateField,
  validateDesign,
  walkGroups,
} from "../../modules/playEngineDesign";
import {
  completeDesign,
  designTargets,
  fieldForTarget,
  missingTargets,
  pruneDesignToRelease,
  readDesignValue,
  scaffoldPe3dDesign,
  writeDesignValue,
} from "../../modules/pe3dDesign";
import { BAR_BG, DESIGN_PANEL_CSS, PANEL_BG, SECTION_DIVIDER, SectionHeader } from "../playEngineDesign/designFields";
import DesignFieldRenderer from "../playEngineDesign/designFieldRenderer";
import DesignOutliner from "../playEngineDesign/designOutliner";
import DialogAddDesignField, { parseOptionLines } from "../playEngineDesign/dialogAddDesignField";
import PlayEngineTabSearch, { matchesTabSearch } from "../playEngineTabSearch";

// The playEngine Design tab's split, and its pane chrome (containerPlayEngineDesign.js).
const SPLIT_STORAGE_KEY = "pe3dDesignSplit";
const DEFAULT_SPLIT = 0.34;
const MIN_SPLIT = 0.15;
const MAX_SPLIT = 0.75;

function readStoredSplit() {
  try {
    const stored = Number(window.localStorage.getItem(SPLIT_STORAGE_KEY));
    if (isFinite(stored) && stored >= MIN_SPLIT && stored <= MAX_SPLIT) return stored;
  } catch (_err) {
    // storage refused: the default split
  }
  return DEFAULT_SPLIT;
}

function SplitHandle({ onPointerDown }) {
  return (
    <div
      onPointerDown={onPointerDown}
      style={{ flex: "0 0 auto", height: 6, cursor: "row-resize", backgroundColor: "#2b2b2b", borderTop: "1px solid #1f1f1f", borderBottom: "1px solid #1f1f1f" }}
    />
  );
}

function InspectorHeader({ title }) {
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        padding: "10px 12px",
        backgroundColor: BAR_BG,
        borderBottom: `1px solid ${SECTION_DIVIDER}`,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>{title}</span>
    </div>
  );
}

function PaneCaption({ title }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        flexShrink: 0,
        backgroundColor: BAR_BG,
        borderTop: `1px solid ${SECTION_DIVIDER}`,
        borderBottom: `1px solid ${SECTION_DIVIDER}`,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>{title}</span>
    </div>
  );
}

const PANE_BUTTON_STYLE = { color: "#aaa", fontSize: "11px", textTransform: "none", minWidth: 0, padding: "2px 8px", backgroundColor: BAR_BG, borderRadius: 4 };
const DIALOG_PAPER = { style: { backgroundColor: PANEL_BG } };

/** The language-override key a row's path is stored under (pe3dLocalization isOverridden). */
function overrideKeyOf(target) {
  if (target.kind === "config") return ["config", target.path];
  if (target.kind === "asset") return ["assets", target.slot];
  if (target.key.startsWith("storeLinks.")) return ["storeLinks", target.key.slice("storeLinks.".length)];
  return ["playable", target.key];
}

/**
 * @param props.editor       usePe3dEditor
 * @param props.canEdit      whether values may be changed (the design itself follows the variant's access)
 * @param props.onNavigateToAsset  (slot) → open the Assets tab
 */
export default function ContainerPe3dDesign({ editor, canEdit, createSnackbar, onNavigateToAsset, isPortrait = true }) {
  const { doc, release, view } = editor;
  const schema = release.bundle.schema;
  const code = release.code;
  const variantID = doc._id;

  // ── The stored design ────────────────────────────────────────────────────
  const [loaded, setLoaded] = useState(null); // { design, contentHash, gameDesign, releases }
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const hashRef = useRef(null);

  const notify = useCallback((message, variant = "info") => createSnackbar && createSnackbar(message, variant, false), [createSnackbar]);

  const load = useCallback(async () => {
    setLoadError("");
    const result = await api.getDesign(variantID);
    if (!result.ok) {
      setLoadError(typeof result.error === "string" ? result.error : `HTTP ${result.status}`);
      return;
    }
    hashRef.current = result.data.contentHash;
    setLoaded(result.data);
  }, [variantID]);

  useEffect(() => {
    setLoaded(null);
    load();
  }, [load, doc.release]);

  // What is shown: the company's saved design, else the release's, else a generated one.
  const rawDesign = useMemo(() => {
    if (!loaded) return null;
    if (loaded.design) return completeDesign(loaded.design, schema);
    if (loaded.gameDesign) return completeDesign(loaded.gameDesign, schema);
    return scaffoldPe3dDesign(schema, { title: doc.game.replace("@pe3d-games/", "") });
  }, [loaded, schema, doc.game]);
  const startedFrom = loaded && !loaded.design && loaded.gameDesign ? "the game's design" : null;

  /** Save a new design; the screen shows it at once and goes back if the save is refused. */
  const persistDesign = useCallback(
    async (next) => {
      const previous = loaded;
      setLoaded((current) => ({ ...current, design: next }));
      setSaving(true);
      const result = await api.saveDesign(variantID, next, hashRef.current);
      setSaving(false);
      if (result.ok) {
        hashRef.current = result.data.contentHash;
        return;
      }
      if (result.status === 409 && result.error && typeof result.error === "object") {
        hashRef.current = result.error.contentHash;
        setLoaded((current) => ({ ...current, design: result.error.design }));
        notify("This design was changed elsewhere meanwhile — it is reloaded; make your change again.", "warning");
        return;
      }
      setLoaded(previous);
      notify(`Could not save the design: ${typeof result.error === "string" ? result.error : `HTTP ${result.status}`}`, "error");
    },
    [loaded, variantID, notify]
  );

  // A base with nothing stored and no design in the release has no design: the
  // starter is offered, not shown, exactly as the playEngine Design tab offers
  // one (containerPlayEngineDesign.js). Showing a generated design as though
  // it were the base's own hid the fact that nobody had made one.
  const hasDesign = Boolean(loaded && (loaded.design || loaded.gameDesign));
  const [generating, setGenerating] = useState(false);
  const generateStarter = async () => {
    setGenerating(true);
    const starter = scaffoldPe3dDesign(schema, { title: doc.game.replace("@pe3d-games/", "") });
    await persistDesign(starter);
    setGenerating(false);
    notify(`Generated a starter design with ${starter.groups.length} groups.`, "success");
  };


  // ── What the release offers ──────────────────────────────────────────────
  const targets = useMemo(() => designTargets(schema), [schema]);
  const effective = useMemo(() => (view ? code.effectiveConfig(schema.config, view.config) : {}), [code, schema, view && view.config]);
  const [showUnused, setShowUnused] = useState(false);
  const design = useMemo(() => {
    const normalized = rawDesign ? normalizeDesign(rawDesign) : null;
    return normalized && !showUnused ? pruneDesignToRelease(normalized, schema) : normalized;
  }, [rawDesign, schema, showUnused]);
  const problems = useMemo(() => (rawDesign ? validateDesign(rawDesign) : []), [rawDesign]);
  const fieldCount = (d) => walkGroups(d).reduce((sum, entry) => sum + entry.group.fields.length, 0);
  const missing = useMemo(() => (rawDesign ? missingTargets(rawDesign, schema) : []), [rawDesign, schema]);

  // ── Outliner state ───────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const allEntries = useMemo(() => (design ? walkGroups(design) : []), [design]);
  const matchingGroupNames = useMemo(
    () => new Set(allEntries.filter((entry) => search.trim() && matchesTabSearch(entry.group.label, search)).map((entry) => entry.group.id)),
    [allEntries, search]
  );
  const matchingEntries = useMemo(
    () =>
      search.trim()
        ? allEntries.filter(
          (entry) =>
            matchingGroupNames.has(entry.group.id) ||
              entry.ancestors.some((id) => matchingGroupNames.has(id)) ||
              entry.group.fields.some((field) => matchesTabSearch(field.label, search) || matchesTabSearch(field.path, search))
        )
        : allEntries,
    [allEntries, search, matchingGroupNames]
  );
  const matchingGroupIds = useMemo(() => {
    const ids = new Set();
    matchingEntries.forEach((entry) => {
      ids.add(entry.group.id);
      entry.ancestors.forEach((id) => ids.add(id));
    });
    return ids;
  }, [matchingEntries]);
  const visibleEntries = useMemo(
    () => (search.trim() ? allEntries.filter((entry) => matchingGroupIds.has(entry.group.id)) : allEntries.filter((entry) => entry.ancestors.every((id) => expandedIds.has(id)))),
    [allEntries, expandedIds, matchingGroupIds, search]
  );
  const selectedGroup = useMemo(() => {
    const found = allEntries.find((entry) => entry.group.id === selectedGroupId && (!search.trim() || matchingEntries.includes(entry)));
    return found ? found.group : _.get(matchingEntries, "0.group", null);
  }, [allEntries, matchingEntries, search, selectedGroupId]);
  const toggleExpanded = useCallback((id) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Editing the tree (every change is a save) ────────────────────────────
  const [renaming, setRenaming] = useState(null);
  const [removingGroup, setRemovingGroup] = useState(null);
  const [addingField, setAddingField] = useState(false);
  const [editingFieldPath, setEditingFieldPath] = useState(null);
  const [optionsDraft, setOptionsDraft] = useState(null);

  const commit = (next) => next && next !== rawDesign && persistDesign(next);
  const handleMove = (draggedId, targetId, position) => {
    if (position === "inside") setExpandedIds((current) => new Set(current).add(targetId));
    commit(moveGroup(rawDesign, draggedId, targetId, position));
  };
  const handleAddGroup = (parentId) => {
    if (parentId) setExpandedIds((current) => new Set(current).add(parentId));
    commit(addGroup(rawDesign, "New Group", parentId));
  };
  const handleRemoveGroup = (id) => {
    const contents = groupContents(rawDesign, id);
    if (contents.fields > 0 || contents.groups > 0) {
      const entry = allEntries.find((candidate) => candidate.group.id === id);
      setRemovingGroup({ id, label: _.get(entry, "group.label", "this group"), contents });
      return;
    }
    commit(removeGroup(rawDesign, id));
  };
  const confirmRemoveGroup = () => {
    if (!removingGroup) return;
    const { id, label, contents } = removingGroup;
    setRemovingGroup(null);
    commit(removeGroup(rawDesign, id, { withContents: true }));
    notify(`Removed "${label}" and ${contents.fields} setting${contents.fields === 1 ? "" : "s"} from the design — the values themselves are untouched.`);
  };
  const handleRenameCommit = () => {
    if (!renaming) return;
    const next = renameGroup(rawDesign, renaming.id, renaming.label);
    setRenaming(null);
    commit(next);
  };
  const isPathTaken = useCallback((path) => designHasFieldPath(rawDesign, path), [rawDesign]);

  // A row added by hand takes its type, bounds and choices from the schema; the form keeps its label and control.
  const fromSchema = (spec) => {
    const target = targets[spec.path];
    if (!target) return spec;
    const base = fieldForTarget(target);
    return { ...base, ...spec, type: base.type, ...(base.options && !spec.options && { options: base.options }) };
  };
  const handleAddField = (spec, groupId) => {
    setAddingField(false);
    const next = addField(rawDesign, groupId, fromSchema(spec));
    if (next === rawDesign) return notify("Could not add that setting — check the path.", "warning");
    setSelectedGroupId(groupId);
    setExpandedIds((current) => new Set(current).add(groupId));
    return commit(next);
  };
  const editingField = useMemo(() => (editingFieldPath ? fieldAtPath(rawDesign, editingFieldPath) : null), [editingFieldPath, rawDesign]);
  const handleUpdateField = (spec, groupId) => {
    if (!editingField) return;
    let next = updateField(rawDesign, editingFieldPath, fromSchema({ ...spec, path: editingFieldPath }));
    if (next === rawDesign) return notify("Could not save that setting.", "warning");
    if (groupId && groupId !== editingField.groupId) {
      next = moveField(next, editingFieldPath, groupId);
      setSelectedGroupId(groupId);
    }
    setEditingFieldPath(null);
    commit(next);
  };
  const handleOptionsCommit = () => {
    if (!optionsDraft) return;
    const located = fieldAtPath(rawDesign, optionsDraft.path);
    setOptionsDraft(null);
    if (located) commit(updateField(rawDesign, optionsDraft.path, { ...located.field, options: parseOptionLines(optionsDraft.text) }));
  };
  const addMissing = () => {
    if (!missing.length) return;
    const next = { ...rawDesign, groups: [...(rawDesign.groups || []), { id: `new-${Date.now()}`, label: "New settings", fields: missing.map(fieldForTarget), groups: [] }] };
    commit(next);
  };
  const copyFrom = async (otherRelease) => {
    const result = await api.getDesign(variantID, otherRelease);
    if (!result.ok || !result.data.design) return notify(`No design for ${otherRelease}.`, "warning");
    return commit(result.data.design);
  };

  // Rows reach memoised renderers: stable handlers that read the latest state.
  const latest = useRef(null);
  latest.current = { rawDesign, persistDesign, setEditingFieldPath, setOptionsDraft, notify };
  const onMoveField = useCallback((path, groupId) => {
    const { rawDesign: d } = latest.current;
    const next = moveField(d, path, groupId);
    if (next !== d) {
      setSelectedGroupId(groupId);
      latest.current.persistDesign(next);
    }
  }, []);
  const onReorderField = useCallback((path, offset) => {
    const { rawDesign: d } = latest.current;
    const next = reorderField(d, path, offset);
    if (next !== d) latest.current.persistDesign(next);
  }, []);
  const onRemoveField = useCallback((path) => {
    const { rawDesign: d } = latest.current;
    const next = removeField(d, path);
    if (next === d) return;
    latest.current.persistDesign(next);
    latest.current.notify(`Removed "${path}" from the design — the value itself is untouched.`);
  }, []);
  const onEditField = useCallback((path) => latest.current.setEditingFieldPath(path), []);
  const onEditFieldOptions = useCallback((path) => {
    const located = fieldAtPath(latest.current.rawDesign, path);
    if (!located) return;
    latest.current.setOptionsDraft({
      path,
      text: (located.field.options || [])
        .map((option) => (_.isPlainObject(option) ? (option.label === undefined || String(option.label) === String(option.value) ? String(option.value) : `${option.value} = ${option.label}`) : String(option)))
        .join("\n"),
    });
  }, []);

  // ── Values ───────────────────────────────────────────────────────────────
  const valueRef = useRef(null);
  valueRef.current = { targets, editor, code, schema };
  const onFieldChange = useCallback((path, value) => {
    const { targets: t, editor: e, code: c, schema: s } = valueRef.current;
    const target = t[path];
    if (!target) return;
    e.editView((v) => writeDesignValue(target, v, target.type === "integer" ? Math.round(value) : value, c, s));
  }, []);
  const onRevert = useCallback((path) => {
    const target = valueRef.current.targets[path];
    if (target) valueRef.current.editor.resetOverride(...overrideKeyOf(target));
  }, []);

  // Asset rows show the slot's file as a thumbnail and open it in the Assets tab.
  const [thumbs, setThumbs] = useState({});
  const assetKey = JSON.stringify((view && view.assets) || {});
  useEffect(() => {
    let cancelled = false;
    const slots = Object.values(targets).filter((target) => target.kind === "asset");
    Promise.all(
      slots.map((target) =>
        release
          .slotThumbnail(target.slot, (view && view.assets && view.assets[target.slot]) || null)
          .then((url) => [target.slot, url])
          .catch(() => [target.slot, null])
      )
    ).then((pairs) => !cancelled && setThumbs(Object.fromEntries(pairs)));
    return () => {
      cancelled = true;
    };
  }, [release, targets, assetKey]);

  // ── Split ────────────────────────────────────────────────────────────────
  const [splitRatio, setSplitRatio] = useState(readStoredSplit);
  const outlinerPaneRef = useRef(null);
  const inspectorPaneRef = useRef(null);
  const startDividerDrag = (event) => {
    event.preventDefault();
    const top = outlinerPaneRef.current;
    const bottom = inspectorPaneRef.current;
    if (!top || !bottom) return;
    const startY = event.clientY;
    const height = top.getBoundingClientRect().height + bottom.getBoundingClientRect().height;
    if (height <= 0) return;
    const startRatio = splitRatio;
    document.body.style.userSelect = "none";
    const onMove = (moveEvent) => setSplitRatio(Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, startRatio + (moveEvent.clientY - startY) / height)));
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
      setSplitRatio((current) => {
        try {
          window.localStorage.setItem(SPLIT_STORAGE_KEY, String(current));
        } catch (_err) {
          // not remembered
        }
        return current;
      });
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const isOverrideMode = editor.localizationSupported && editor.language !== "en";
  const otherReleases = ((loaded && loaded.releases) || []).filter((item) => item !== doc.release);

  function renderFields() {
    if (!selectedGroup) return <div style={{ padding: "20px 16px", fontSize: 13, color: "#888" }}>Select a group to edit its settings.</div>;
    const addButton = (
      <div style={{ display: "flex", gap: 6, padding: "6px 5px 2px 10px" }}>
        <Button size="small" disabled={saving} onClick={() => setAddingField(true)} startIcon={<AddIcon style={{ fontSize: 14 }} />} style={PANE_BUTTON_STYLE}>
          Setting
        </Button>
      </div>
    );
    if (selectedGroup.fields.length === 0) {
      return (
        <>
          <InspectorHeader title={selectedGroup.label} />
          {addButton}
          <SectionHeader title="Properties" />
          <div style={{ padding: "8px 12px", fontSize: 12, color: "#8a8d93" }}>
            {selectedGroup.groups.length > 0 ? "A folder — its settings are in the groups inside it." : "No settings in this group yet."}
          </div>
        </>
      );
    }
    const groupNameMatches = matchingGroupNames.has(selectedGroup.id) || allEntries.some((entry) => entry.group.id === selectedGroup.id && entry.ancestors.some((id) => matchingGroupNames.has(id)));
    return (
      <>
        <InspectorHeader title={selectedGroup.label} />
        {addButton}
        <SectionHeader title="Properties" />
        <div style={{ padding: "2px 4px 16px" }}>
          {selectedGroup.fields.map((field, index) => {
            if (search.trim() && !groupNameMatches && !matchesTabSearch(field.label, search) && !matchesTabSearch(field.path, search)) return null;
            const target = targets[field.path];
            const isAsset = field.type === "asset";
            return (
              <DesignFieldRenderer
                key={field.path}
                field={field}
                value={target && !isAsset ? readDesignValue(target, view, effective) : undefined}
                present={Boolean(target)}
                // A portrait/landscape pair shows the half the preview is in.
                isPortrait={isPortrait}
                disabled={!canEdit}
                assetEntry={isAsset && target ? { type: target.def.kind } : null}
                assetThumbnail={isAsset && target ? thumbs[target.slot] || null : null}
                onNavigateToAsset={isAsset && target && onNavigateToAsset ? () => onNavigateToAsset(target.slot) : null}
                onChange={onFieldChange}
                language={editor.language}
                // pe2d's asset rows say "shared across languages"; a pe3d slot has a file per language.
                isOverrideMode={isOverrideMode && !isAsset}
                isOverridden={Boolean(target && isOverrideMode && editor.isOverridden(...overrideKeyOf(target)))}
                onRevert={onRevert}
                groupEntries={allEntries}
                currentGroupId={selectedGroup.id}
                onEditField={onEditField}
                onEditFieldOptions={onEditFieldOptions}
                onReorderField={onReorderField}
                canMoveUp={index > 0}
                canMoveDown={index < selectedGroup.fields.length - 1}
                onMoveField={onMoveField}
                onRemoveField={onRemoveField}
              />
            );
          })}
        </div>
      </>
    );
  }

  if (loadError) return <div style={{ padding: 16, fontSize: 13, color: "#e57373" }}>Could not load the design: {loadError}</div>;
  if (!loaded || !design) return <div style={{ padding: 16, fontSize: 13, color: "#888" }}>Loading the design…</div>;

  return (
    <div className="design-props-panel" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, backgroundColor: PANEL_BG }}>
      <style>{DESIGN_PANEL_CSS}</style>
      <PlayEngineTabSearch value={search} onChange={setSearch} />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          rowGap: 6,
          padding: "8px 10px",
          flexShrink: 0,
          backgroundColor: BAR_BG,
          borderBottom: `1px solid ${SECTION_DIVIDER}`,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>{(rawDesign && rawDesign.title) || "Design"}</span>
        <span style={{ fontSize: 11, color: "#8a8d93", fontFamily: "monospace" }}>{doc.release}</span>
        <span style={{ fontSize: 11, color: "#8a8d93" }}>
          {allEntries.length}/{walkGroups(normalizeDesign(rawDesign)).length} groups · {fieldCount(design)}/{fieldCount(normalizeDesign(rawDesign))} fields
        </span>
        <Button size="small" onClick={() => setShowUnused((value) => !value)} style={PANE_BUTTON_STYLE}>
          {showUnused ? "Hide unused" : "Show unused"}
        </Button>
        {otherReleases.length > 0 && (
          <Tooltip title="Replace this design with the one laid out for another release; rows its schema lacks show as unused">
            <Select
              value=""
              displayEmpty
              disabled={saving}
              onChange={(event) => event.target.value && copyFrom(event.target.value)}
              MenuProps={{ PaperProps: { style: { backgroundColor: PANEL_BG, color: "#e8e8e8" } } }}
              style={{ fontSize: 12, color: "#fff", minWidth: 120 }}
            >
              <MenuItem value="" disabled style={{ fontSize: 12 }}>
                Copy from…
              </MenuItem>
              {otherReleases.map((item) => (
                <MenuItem key={item} value={item} style={{ fontSize: 12 }}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        )}
        <span style={{ flex: 1 }} />
        {saving && <CircularProgress size={14} />}
        <Tooltip title="Reload the design">
          <span>
            <Button size="small" disabled={saving} onClick={load} style={{ ...PANE_BUTTON_STYLE, padding: 2 }}>
              <RefreshIcon style={{ fontSize: 16 }} />
            </Button>
          </span>
        </Tooltip>
      </div>

      {loaded && !hasDesign && (
        <div style={{ padding: "20px 16px", maxWidth: 460 }}>
          <div style={{ fontSize: 13, color: "#c9ccd2", marginBottom: 6 }}>This base has no design yet.</div>
          <div style={{ fontSize: 12, color: "#8a8d93", lineHeight: 1.5, marginBottom: 14 }}>
            A design says what each setting IS — a colour, a position, a 0–1 knob — so this tab can
            show the right control instead of a text box. Generating one reads the release&apos;s
            schema and groups every setting and slot it has; you then delete what is not worth
            showing and label the rest.
          </div>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={saving || generating}
            onClick={generateStarter}
            startIcon={generating ? <CircularProgress size={13} /> : <GenerateIcon />}
          >
            {generating ? "Generating…" : "Generate starter design"}
          </Button>
        </div>
      )}

      {hasDesign && startedFrom && (
        <div style={{ flexShrink: 0, padding: "6px 10px", fontSize: 11, color: "#8a8d93", borderBottom: `1px solid ${SECTION_DIVIDER}` }}>
          Showing {startedFrom} for {doc.release}. It becomes this base&apos;s own with your first change.
        </div>
      )}
      {problems.length > 0 && (
        <div style={{ flexShrink: 0, padding: "6px 10px", fontSize: 11, color: "#c9a25b", backgroundColor: "rgba(201, 162, 91, 0.08)", borderBottom: `1px solid ${SECTION_DIVIDER}` }}>
          {problems.length} problem{problems.length === 1 ? "" : "s"} in this design — first: {problems[0].where}: {problems[0].message}
        </div>
      )}

      {hasDesign && (
        <div ref={outlinerPaneRef} style={{ flexGrow: splitRatio, flexShrink: 1, flexBasis: 0, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <PaneCaption title="Groups" />
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 0" }}>
            <div style={{ display: "flex", gap: 6, padding: "4px 5px 4px 10px", flexWrap: "wrap" }}>
              <Button size="small" disabled={saving} onClick={() => handleAddGroup(null)} startIcon={<AddIcon style={{ fontSize: 14 }} />} style={PANE_BUTTON_STYLE}>
              Group
              </Button>
              <Tooltip title={missing.length ? `Add a group with the ${missing.length} setting(s) the design does not show yet` : "The design shows everything this release has"}>
                <span>
                  <Button size="small" disabled={saving || !missing.length} onClick={addMissing} startIcon={<GenerateIcon style={{ fontSize: 14 }} />} style={PANE_BUTTON_STYLE}>
                  From release{missing.length ? ` (${missing.length})` : ""}
                  </Button>
                </span>
              </Tooltip>
            </div>
            <DesignOutliner
              entries={visibleEntries}
              selectedId={selectedGroup ? selectedGroup.id : null}
              expandedIds={search.trim() ? matchingGroupIds : expandedIds}
              canEdit={!saving && !search.trim()}
              onSelect={setSelectedGroupId}
              onToggle={toggleExpanded}
              onMove={handleMove}
              onAddChild={handleAddGroup}
              onRename={(id, label) => setRenaming({ id, label })}
              onRemove={handleRemoveGroup}
            />
            {search.trim() && matchingEntries.length === 0 && <div style={{ padding: 12, color: "#888", fontSize: 12 }}>No settings match this search.</div>}
          </div>
        </div>
      )}

      {hasDesign && <SplitHandle onPointerDown={startDividerDrag} />}

      {hasDesign && (
        <div ref={inspectorPaneRef} style={{ flexGrow: 1 - splitRatio, flexShrink: 1, flexBasis: 0, minHeight: 0, overflowY: "auto", backgroundColor: PANEL_BG }}>
          {renderFields()}
        </div>
      )}

      <DialogAddDesignField
        open={addingField || Boolean(editingField)}
        groupEntries={allEntries}
        defaultGroupId={selectedGroup ? selectedGroup.id : null}
        variables={effective}
        assets={Object.fromEntries(Object.values(targets).filter((target) => target.kind === "asset").map((target) => [target.slot, { type: target.def.kind }]))}
        isPathTaken={isPathTaken}
        editingField={editingField ? editingField.field : null}
        editingGroupId={editingField ? editingField.groupId : null}
        onClose={() => {
          setAddingField(false);
          setEditingFieldPath(null);
        }}
        onSubmit={editingField ? handleUpdateField : handleAddField}
      />

      <Dialog open={Boolean(optionsDraft)} onClose={() => setOptionsDraft(null)} maxWidth="xs" fullWidth PaperProps={DIALOG_PAPER}>
        <DialogTitle>Allowed values</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={14}
            size="small"
            value={optionsDraft ? optionsDraft.text : ""}
            onChange={(event) => {
              const text = event.target.value;
              setOptionsDraft((current) => ({ ...current, text }));
            }}
            onKeyDown={(event) => event.stopPropagation()}
            InputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
            helperText="One value per line. Write value = Label to show a different label. Leave it empty to go back to a text box."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsDraft(null)}>Cancel</Button>
          <Button color="primary" onClick={handleOptionsCommit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removingGroup)} onClose={() => setRemovingGroup(null)} maxWidth="xs" fullWidth PaperProps={DIALOG_PAPER}>
        <DialogTitle>Delete this group?</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 13, color: "#c9ccd2", lineHeight: 1.5 }}>
            &quot;{_.get(removingGroup, "label")}&quot; holds {_.get(removingGroup, "contents.fields", 0)} setting
            {_.get(removingGroup, "contents.fields", 0) === 1 ? "" : "s"}
            {_.get(removingGroup, "contents.groups", 0) > 0 ? ` in ${_.get(removingGroup, "contents.groups", 0)} group${_.get(removingGroup, "contents.groups", 0) === 1 ? "" : "s"} inside it` : ""}. All of it goes.
          </div>
          <div style={{ fontSize: 12, color: "#8a8d93", lineHeight: 1.5, marginTop: 10 }}>
            This removes rows from the design, not values from the variant: each keeps its value and stays editable in Config, Assets and Playable.
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingGroup(null)}>Cancel</Button>
          <Button style={{ color: "#e57373" }} onClick={confirmRemoveGroup}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(renaming)} onClose={() => setRenaming(null)} maxWidth="xs" fullWidth PaperProps={DIALOG_PAPER}>
        <DialogTitle>Rename group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={renaming ? renaming.label : ""}
            onChange={(event) => {
              const label = event.target.value;
              setRenaming((current) => ({ ...current, label }));
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter") handleRenameCommit();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>Cancel</Button>
          <Button color="primary" onClick={handleRenameCommit}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
